<template>
  <div class="addon">
    <img :src="`/img/addons/${info.icon}`" alt="" class="addon__img">
    <p class="addon__title">
      {{ t(`addons.${propsKey}.title`) }}
    </p>
    <p class="addon__desc">
      {{ t(`addons.${propsKey}.desc`) }}
    </p>
    <div v-if="info.price" class="addon__price">
      <ui-token
          :tokens="info.price"
          icon-right
      />
      <span> / {{ t('month') }} </span>
    </div>
    <template v-else>
      <span class="addon__tag">
        {{ t('standard_and_pro_plans') }}
      </span>
      <div class="addon__frequency">
        <p class="addon__title">
          {{ t('data_update_frequency') }}
        </p>
        <div class="frequency__wrapper">
          <div class="frequency__item">
            <span class="frequency__item-time">
              {{ time[UserPlan.Standard] }}
            </span>
            <a
                class="frequency__item-link standard"
                href="#pricing__tumbler"
            >
              {{ t('plan_value', {plan: t(`plans.${UserPlan.Standard}.title`)}) }}
              <svg height="10" width="10">
                <use xlink:href="/icons/sprite.svg#i-arrow-right-top"></use>
              </svg>
            </a>
          </div>
          <div class="frequency__item">
            <span class="frequency__item-time">
              {{ time[UserPlan.Pro] }}
            </span>
            <a
                class="frequency__item-link pro"
                href="#pricing__tumbler"
            >
              {{ t('plan_value', {plan: t(`plans.${UserPlan.Pro}.title`)}) }}
              <svg height="10" width="10">
                <use xlink:href="/icons/sprite.svg#i-arrow-right-top"></use>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </template>
    <button
        class="btn btn-primary btn-violet w-100 fw500"
        @click="openModal(info.modalName)"
    >
      {{ t('discover_details') }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import type {AddonInfoLocal} from "~/data/addons";
import {AddonEnumLocal} from "~/data/addons";
import {UserPlan} from "~/graphql/generated/schema-types";
import {PLANS} from "~/data/plans";
import {useModalStore} from "~/store/modal";

const props = defineProps<{
  info: AddonInfoLocal,
  propsKey: string
}>()

const {t} = useI18n()
const {openModal} = useModalStore()

function getTime(time: number) {
  if (time / 3600000 < 1) {
    return t('minutes', {n: time / 60000})
  }
  return t('hours', {n: time / 3600000})
}

const time = computed(() => {
  return {
    [UserPlan.Standard]: getTime(PLANS[UserPlan.Standard][props.propsKey as AddonEnumLocal]),
    [UserPlan.Pro]: getTime(PLANS[UserPlan.Pro][props.propsKey as AddonEnumLocal]),
  }
})
</script>

<style lang="scss" scoped>
.addon {
  border-radius: 12px;
  border: 1px solid var(--surfaces-outline);
  background: var(--surface-surface);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &__img {
    width: 44px;
    height: 44px;
    margin-bottom: 24px;
    border-radius: 12px;
  }

  &__title {
    color: var(--white);
    font-weight: 600;
    margin-bottom: 8px;
  }

  &__desc {
    color: var(--surfaces-on-surface-variable);
    margin-bottom: 36px;
  }

  &__tag {
    margin-top: auto;
    margin-bottom: 20px;
    border-radius: 100px;
    background: var(--accent-secondary-container);
    padding: 2px 12px;
    color: var(--accent-on-surface);

  }

  &__price {
    margin-top: auto;
    margin-bottom: 20px;

    &:deep(.price) {
      font-size: 16px;
    }
  }

  &__frequency {
    display: none;
  }

  @media screen and (max-width: 979px) {
    &__img {
      margin-bottom: 16px;
    }
    &__desc {
      margin-bottom: 20px;
    }
    &__tag {
      display: none;
    }
    &__frequency {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 20px;

      .frequency {
        &__wrapper {
          display: flex;
          gap: 24px;

        }

        &__item {
          display: flex;
          flex-direction: column;
          gap: 4px;

          &-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            line-height: 18px;

            &.standard {
              color: var(--esports);
            }

            &.pro {
              color: #F8C1FF;
            }
          }
        }
      }
    }
  }
}
</style>
